/** Public STUN servers used to discover the browser's own ICE candidates. */
export const ICE_SERVERS: RTCIceServer[] = [
    {
        urls: [
            'stun:stun.l.google.com:19302',
            'stun:stun1.l.google.com:19302',
            'stun:stun2.l.google.com:19302',
            'stun:stun3.l.google.com:19302',
            'stun:stun4.l.google.com:19302',
        ],
    },
];

/** Upper bound on the wait below, so a slow or unreachable STUN server cannot stall the stream. */
export const ICE_GATHERING_TIMEOUT_MS = 3000;

/** How long to wait before rebuilding a peer connection that failed or dropped. */
export const STREAM_RESTART_DELAY_MS = 2000;

/**
 * Resolves once the connection has finished gathering ICE candidates.
 *
 * Signalling with go2rtc is a single HTTP round trip: it answers with a complete SDP and there is
 * no channel to trickle candidates afterwards. So the offer has to carry the browser's candidates
 * with it — sending it early leaves go2rtc with nothing to connect back to, which shows up as a
 * stream that takes a long time to start or never starts at all.
 *
 * Resolves on timeout rather than rejecting: a partial candidate list still connects in most
 * networks, so it is always better to send the offer than to abandon the stream.
 */
export const waitForIceGatheringComplete = (
    connection: RTCPeerConnection,
    timeoutMs: number = ICE_GATHERING_TIMEOUT_MS,
): Promise<void> =>
    new Promise((resolve) => {
        if (connection.iceGatheringState === 'complete') {
            resolve();
            return;
        }

        // Aborting detaches the listener on both the completed and the timed-out path.
        const listener = new AbortController();
        const finish = () => {
            listener.abort();
            resolve();
        };

        const timer = setTimeout(finish, timeoutMs);

        connection.addEventListener(
            'icegatheringstatechange',
            () => {
                if (connection.iceGatheringState !== 'complete') return;

                clearTimeout(timer);
                finish();
            },
            { signal: listener.signal },
        );
    });
